'use client'

// SUMMARY: Cycles a single word in place (e.g. "für" <-> "aus") with a fade/slide transition; box width tracks the current word so trailing text stays correctly spaced

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

interface SwappingWordProps {
	words: string[]
	colors?: string[]
	intervalMs?: number
}

export function SwappingWord({ words, colors, intervalMs = 2600 }: SwappingWordProps) {
	const [index, setIndex] = useState(0)
	const [width, setWidth] = useState<number | null>(null)
	const measureRef = useRef<HTMLSpanElement>(null)

	// measure the current word so the container width (and the text after it) tracks word length
	useEffect(() => {
		const update = () => {
			if (measureRef.current) setWidth(measureRef.current.getBoundingClientRect().width)
		}
		update()
		const fonts = (document as Document & { fonts?: FontFaceSet }).fonts
		fonts?.ready.then(update)
		window.addEventListener('resize', update)
		return () => window.removeEventListener('resize', update)
	}, [index, words])

	useEffect(() => {
		if (words.length < 2) return
		const id = setInterval(() => setIndex((prev) => (prev + 1) % words.length), intervalMs)
		return () => clearInterval(id)
	}, [words.length, intervalMs])

	return (
		<span
			className="relative inline-block whitespace-nowrap align-baseline transition-[width] duration-500 ease-out motion-reduce:transition-none"
			style={{ width: width ?? undefined }}
		>
			{/* in-flow, invisible measurer = current word: gives correct width before JS runs and a measurement target */}
			<span ref={measureRef} aria-hidden className="invisible">
				{words[index]}
			</span>
			{words.map((word, i) => (
				<span
					key={word}
					aria-hidden={i !== index}
					className={cn(
						'absolute left-0 top-0 transition-all duration-500 ease-out motion-reduce:transition-none',
						colors?.[i],
						i === index ? 'translate-y-0 opacity-100 blur-0' : '-translate-y-2 opacity-0 blur-[2px]'
					)}
				>
					{word}
				</span>
			))}
		</span>
	)
}
